import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { aiPodService, type AiPod, type ChatbotTemplate } from '../../../../services/aiPodService';
import { Button } from '../../../../components/ui/Button';
import { TextArea, TextField } from '../../../../components/ui/Field';
import ThemeColorSettings from '../../../../components/chatbot/tabs/ThemesTab/ThemeColorSettings';
import ThemePreview from '../../../../components/chatbot/tabs/ThemesTab/ThemePreview';
import { defaultTheme, mapToApiSettings, readThemeFromApiShape } from '../../../../components/chatbot/tabs/ThemesTab/themeUtils';
import type { ThemeState } from '../../../../components/chatbot/types/chatbot.types';

const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-agentic-text-secondary';
const inputCls =
  'w-full rounded-lg border border-agentic-gray-300 bg-white px-3 py-2 text-sm text-agentic-text-primary focus:border-kv-agentic-blue focus:outline-none focus:ring-2 focus:ring-kv-agentic-blue/20';

/**
 * The chatbot a chat/both pod creates for each subscriber: name, greeting and look.
 * Saved on the pod; subscribers already provisioned keep their own copy.
 */
const ChatbotTemplateEditor: React.FC<{ pod: AiPod; onSaved?: (pod: AiPod) => void }> = ({ pod, onSaved }) => {
  const template = (pod.chatbotTemplate ?? {}) as ChatbotTemplate;
  const [name, setName] = useState(template.name ?? pod.name);
  const [description, setDescription] = useState(template.description ?? '');
  const [greeting, setGreeting] = useState(template.greeting ?? 'Hello');
  const [placeholder, setPlaceholder] = useState(template.placeholder ?? 'Type here...');
  const [theme, setTheme] = useState<ThemeState>(
    () => readThemeFromApiShape({ themeConfig: template.themeConfig }) ?? defaultTheme()
  );
  const [version, setVersion] = useState(pod.version);
  const [saving, setSaving] = useState(false);

  useEffect(() => setVersion(pod.version), [pod.version]);

  const save = async () => {
    if (!name.trim()) {
      toast.error('Chatbot name is required');
      return;
    }
    setSaving(true);
    try {
      const updated = await aiPodService.updateAiPod(pod.id, {
        version,
        chatbotTemplate: {
          name: name.trim(),
          description: description.trim() || undefined,
          greeting,
          placeholder,
          themeConfig: mapToApiSettings(theme),
        },
      });
      const next = ((updated as any)?.data ?? updated) as AiPod;
      setVersion(next.version);
      toast.success('Chat configuration saved');
      onSaved?.(next);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to save chat configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-agentic-text-secondary">
        Each tenant that subscribes to <strong>{pod.name}</strong> gets a chatbot with these settings and the pod's agents.
        Changes here apply to new subscribers; existing subscribers keep their chatbot and can adjust it themselves.
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Chatbot name</label>
          <TextField className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Greeting message</label>
          <TextField className={inputCls} value={greeting} onChange={(e) => setGreeting(e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Description</label>
          <TextArea className={`${inputCls} resize-none`} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Input placeholder</label>
          <TextField className={inputCls} value={placeholder} onChange={(e) => setPlaceholder(e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ThemeColorSettings theme={theme} onUpdate={(key, value) => setTheme((t) => ({ ...t, [key]: value }))} />
        <ThemePreview theme={theme} chatbot={{ name, imgLink: null }} />
      </div>
      <div className="flex justify-end gap-3">
        <Button variant="ghost" type="button" onClick={() => setTheme(defaultTheme())}>
          Reset colours
        </Button>
        <Button variant="primary" type="button" loading={saving} onClick={save}>
          Save chat configuration
        </Button>
      </div>
    </div>
  );
};

export default ChatbotTemplateEditor;
